/**
 * Demo Tracking Service
 * Send demo session data to licensing dashboard
 */

const WORKER_URL = 'https://qawaem-platform.qawaemaicode.workers.dev';

class DemoTrackingService {
  constructor() {
    this.sessionId = null;
    this.activityTimer = null;
  }

  /**
   * Start a new demo session
   */
  async startSession(fullName, phone, companyName, selectedPackage) {
    try {
      console.log('🎯 Starting demo session tracking...');
      
      const response = await fetch(`${WORKER_URL}/functions/demo-sessions/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName,
          phone,
          companyName,
          selectedPackage
        })
      });

      const data = await response.json();
      
      if (data.success) {
        this.sessionId = data.sessionId;
        console.log('✅ Demo session started:', this.sessionId);
        
        // Start activity tracking every 30 seconds
        this.startActivityTracking();
        
        // Track page views
        this.trackPageViews();
        
        return this.sessionId;
      } else {
        console.error('❌ Failed to start demo session:', data.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Error starting demo session:', error);
      return null;
    }
  }

  /**
   * Log activity (page view or action)
   */
  async logActivity(activityType, pageRoute, actionDetails = null) {
    if (!this.sessionId) return;

    try {
      await fetch(`${WORKER_URL}/functions/demo-sessions/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          activityType,
          pageRoute,
          actionDetails
        })
      });
    } catch (error) {
      console.error('❌ Error logging activity:', error);
    }
  }

  /**
   * Track page views using hash change
   */
  trackPageViews() {
    // Track initial page
    this.logActivity('page_view', window.location.hash || '#/');

    // Track hash changes
    window.addEventListener('hashchange', () => {
      this.logActivity('page_view', window.location.hash);
    });
  }

  /**
   * Start periodic activity tracking (heartbeat)
   */
  startActivityTracking() {
    // Send heartbeat every 30 seconds to update last_activity_at
    this.activityTimer = setInterval(() => {
      this.logActivity('heartbeat');
    }, 30000);
  }

  /**
   * Track user actions (create, edit, delete)
   */
  trackAction(actionType, details) {
    this.logActivity('action', window.location.hash, {
      actionType,
      ...details
    });
  }

  /**
   * End the demo session
   */
  async endSession() {
    if (!this.sessionId) return;

    try {
      console.log('🎯 Ending demo session...');
      
      // Stop activity tracking
      if (this.activityTimer) {
        clearInterval(this.activityTimer);
        this.activityTimer = null;
      }

      const response = await fetch(`${WORKER_URL}/functions/demo-sessions/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.sessionId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ Demo session ended. Duration: ${data.totalSeconds}s`);
        this.sessionId = null;
      } else {
        console.error('❌ Failed to end demo session:', data.error);
      }
    } catch (error) {
      console.error('❌ Error ending demo session:', error);
    }
  }
}

// Create singleton instance
const demoTrackingService = new DemoTrackingService();

export default demoTrackingService;

